import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  Menu,
  MenuItem,
  Paper,
  Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  PlayArrow as TestRunIcon,
  Description as DocIcon,
  RocketLaunch as LaunchIcon,
  History as HistoryIcon,
  DeleteOutline as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import LaunchScanDialog from './LaunchScanDialog';
import LeadInline from './LeadInline';

interface Rule {
  rule_id: string;
  name: string;
  description: string;
  status: string;
  jurisdiction: string;
  sector: string;
  archetype: string;
  subject_type: string;
  origin: string;
  current_version: number;
  max_fires_per_scan?: number;
  claim: string;
  cohort: string;
  scope: string;
  inline_sql: string;
  parameters: Record<string, any>;
  disconfirmations: any[];
  citations: any[];
  dependencies: any[];
  // Files no longer come from /rules/<id>/ — Documents tab fetches them
  // separately from /rules/<id>/files/ on demand.
  sharepoint_folder_url: string;
  approved_at?: string | null;
  created_at: string;
  updated_at: string;
}

interface Version {
  version: number;
  change_summary: string;
  created_at: string;
  created_by?: string | null;
}

const RuleDetail = ({ ruleId, onRuleMutated }: {
  ruleId: string;
  /** Called after any change that affects the rule's list-row display
   *  (status, name, version) so the middle-column list can re-fetch. */
  onRuleMutated?: () => void;
}) => {
  const navigate = useNavigate();
  const [rule, setRule] = useState<Rule | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Wrap setRule so any in-place rule mutation also notifies the parent.
  const applyRuleChange = (r: Rule) => {
    setRule(r);
    onRuleMutated?.();
  };
  const [tab, setTab] = useState(0);
  const [launchOpen, setLaunchOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  // Cache the SharePoint file list so that tab-switching back to Documents
  // doesn't re-incur the ~900ms Graph round-trip. null = not yet fetched
  // for this rule; refetch implicit when the rule_id changes (handled in
  // the cache-clear effect below).
  const [docsFiles, setDocsFiles] = useState<any[] | null>(null);
  useEffect(() => {
    // Invalidate the cache when the user navigates to a different rule.
    setDocsFiles(null);
  }, [ruleId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setRule(null);
    api
      .get(`/explore/rules/${ruleId}/`)
      .then((res) => { if (!cancelled) setRule(res.data); })
      .catch((err) => {
        if (!cancelled) setError(err?.response?.data?.error || err.message || 'failed');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [ruleId]);

  if (loading) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error || !rule) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="error">{error || 'Rule not found'}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ p: 3, pb: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>{rule.name || rule.rule_id}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {rule.rule_id} · v{rule.current_version}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.75, mt: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
              <StatusChip rule={rule} onChanged={applyRuleChange} />
              {rule.jurisdiction && <Chip label={rule.jurisdiction} size="small" variant="outlined" />}
              {rule.sector && <Chip label={rule.sector} size="small" variant="outlined" />}
              {rule.archetype && <Chip label={rule.archetype} size="small" variant="outlined" />}
              <Chip label={`subject: ${rule.subject_type}`} size="small" variant="outlined" />
              {rule.approved_at && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  approved {new Date(rule.approved_at).toLocaleDateString()}
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<LaunchIcon />}
              onClick={() => setLaunchOpen(true)}
              sx={{ textTransform: 'none' }}
            >
              Launch Scan
            </Button>
            <Button
              variant="text"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteOpen(true)}
              sx={{ textTransform: 'none' }}
            >
              Delete
            </Button>
          </Box>
          <LaunchScanDialog
            open={launchOpen}
            onClose={() => setLaunchOpen(false)}
            preselectedRuleIds={[rule.rule_id]}
            // Stay on the rule; jump to the Scans tab so the new scan shows
            // up at the top of the per-rule scan history.
            onLaunched={() => { setLaunchOpen(false); setTab(2); }}
          />
          <DeleteRuleDialog
            open={deleteOpen}
            onClose={() => setDeleteOpen(false)}
            rule={rule}
            onDeleted={() => { onRuleMutated?.(); navigate('/explore/rules', { replace: true }); }}
          />
        </Box>

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            mt: 2, mb: -0.25,
            // History sits at the far right, separated from the workflow tabs.
            '& .MuiTabs-flexContainer': { gap: 0 },
            '& [data-tab-anchor="right"]': { marginLeft: 'auto' },
          }}
        >
          <Tab label="Overview" sx={{ textTransform: 'none' }} />
          <Tab label="Iterate" sx={{ textTransform: 'none' }} />
          <Tab label="Results" sx={{ textTransform: 'none' }} />
          <Tab label="Documents" sx={{ textTransform: 'none' }} />
          <Tab label="History" data-tab-anchor="right" sx={{ textTransform: 'none' }} />
        </Tabs>
      </Box>

      <Box sx={{ p: 3 }}>
        {tab === 0 && <OverviewPanel rule={rule} navigate={navigate} />}
        {tab === 1 && <IteratePanel rule={rule} onRuleChanged={applyRuleChange} />}
        {tab === 2 && <ResultsPanel rule={rule} navigate={navigate} />}
        {tab === 3 && (
          <DocumentsPanel
            rule={rule}
            cachedFiles={docsFiles}
            onFilesLoaded={setDocsFiles}
          />
        )}
        {tab === 4 && <HistoryPanel rule={rule} />}
      </Box>
    </Box>
  );
};

// ===== Overview =====

const OverviewPanel = ({ rule, navigate }: { rule: Rule; navigate: ReturnType<typeof useNavigate> }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 920 }}>
    {rule.description && (
      <Section title="Description">
        <Typography variant="body1">{rule.description}</Typography>
      </Section>
    )}

    <Section title="Hypothesis">
      <Box sx={{ display: 'grid', gridTemplateColumns: '110px 1fr', rowGap: 1.5, columnGap: 2 }}>
        <Typography variant="body2" color="text.secondary">Claim</Typography>
        <Typography variant="body1">{rule.claim || <em style={{ color: '#999' }}>not set</em>}</Typography>
        <Typography variant="body2" color="text.secondary">Cohort</Typography>
        <Typography variant="body1">{rule.cohort || <em style={{ color: '#999' }}>not set</em>}</Typography>
        <Typography variant="body2" color="text.secondary">Scope</Typography>
        <Typography variant="body1">{rule.scope || <em style={{ color: '#999' }}>not set</em>}</Typography>
      </Box>
    </Section>

    {rule.dependencies?.length > 0 && (
      <Section title="Datasets used">
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          {rule.dependencies.map((d: any) => (
            <Paper
              key={d.dataset_id}
              variant="outlined"
              onClick={() => navigate(`/explore/datasets/${d.dataset_id}`)}
              sx={{
                px: 2, py: 1.5, minWidth: 220, cursor: 'pointer',
                '&:hover': { borderColor: 'primary.main', backgroundColor: 'rgba(58, 68, 82, 0.05)' },
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{d.dataset_id}</Typography>
              <Typography variant="caption" color="text.secondary">
                {d.required ? 'required' : 'optional'} · {d.status}
              </Typography>
              {d.display_name && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                  {d.display_name}
                </Typography>
              )}
            </Paper>
          ))}
        </Box>
      </Section>
    )}

    {rule.disconfirmations?.length > 0 && (
      <Section title="Disconfirmations">
        {rule.disconfirmations.map((d: any, i: number) => (
          <Box key={i} sx={{ mb: 1.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{d.id}</Typography>
            <Typography variant="body2" color="text.secondary">{d.description}</Typography>
            {d.check && (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.25, fontStyle: 'italic' }}>
                Check: {d.check}
              </Typography>
            )}
          </Box>
        ))}
      </Section>
    )}

    {rule.citations?.length > 0 && (
      <Section title="Citations">
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Source</TableCell>
                <TableCell>Section</TableCell>
                <TableCell>Page</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rule.citations.map((c: any, i: number) => (
                <TableRow
                  key={i}
                  hover
                  onClick={() => c.source_id && navigate(`/explore/statutes/${c.source_id}`)}
                  sx={{ cursor: c.source_id ? 'pointer' : 'default' }}
                >
                  <TableCell>
                    <Typography
                      variant="body2"
                      color={c.source_id ? 'primary' : 'inherit'}
                      sx={{ '&:hover': { textDecoration: c.source_id ? 'underline' : 'none' } }}
                    >
                      {c.source_display_name || c.source_id}
                    </Typography>
                  </TableCell>
                  <TableCell>{c.section_path}</TableCell>
                  <TableCell>{c.manual_page ? `p.${c.manual_page}` : ''}{c.pdf_page ? ` (pdf p.${c.pdf_page})` : ''}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Section>
    )}
  </Box>
);

// ===== Iterate =====

const IteratePanel = ({ rule, onRuleChanged }: { rule: Rule; onRuleChanged: (r: Rule) => void }) => {
  const [parametersText, setParametersText] = useState(JSON.stringify(rule.parameters || {}, null, 2));
  const [parseError, setParseError] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<any | null>(null);

  const runTest = async () => {
    let overrides: Record<string, any> = {};
    try {
      overrides = parametersText.trim() ? JSON.parse(parametersText) : {};
      setParseError(null);
    } catch (e: any) {
      setParseError('Invalid JSON: ' + e.message);
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.post(`/explore/rules/${rule.rule_id}/test/`, {
        parameters_override: overrides,
        limit: 20,
      });
      setTestResult(res.data);
    } catch (err: any) {
      setTestResult({ error: err?.response?.data?.error || err.message });
    } finally {
      setTesting(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 920 }}>
      <Section title="Parameters">
        <TextField
          multiline
          fullWidth
          minRows={4}
          value={parametersText}
          onChange={(e) => setParametersText(e.target.value)}
          sx={{ fontFamily: 'monospace', '& textarea': { fontFamily: 'monospace', fontSize: '0.85rem' } }}
        />
        {parseError && <Typography color="error" variant="caption">{parseError}</Typography>}
        <Box sx={{ mt: 1.5 }}>
          <Button
            variant="contained"
            startIcon={testing ? <CircularProgress size={16} color="inherit" /> : <TestRunIcon />}
            onClick={runTest}
            disabled={testing}
            sx={{ textTransform: 'none' }}
          >
            {testing ? 'Running…' : 'Test Run'}
          </Button>
        </Box>
      </Section>

      {testResult && (
        <Section title="Test results">
          {testResult.error && <Typography color="error">{testResult.error}</Typography>}
          {!testResult.error && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {testResult.row_count} row{testResult.row_count === 1 ? '' : 's'} in {testResult.duration_ms}ms
                {testResult.more_available && ' (more available)'}
              </Typography>
              {(testResult.rows || []).length > 0 && (
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {Object.keys(testResult.rows[0]).map((k) => (
                          <TableCell key={k}>{k}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {testResult.rows.map((r: any, i: number) => (
                        <TableRow key={i}>
                          {Object.keys(testResult.rows[0]).map((k) => (
                            <TableCell key={k}>{formatCell(r[k])}</TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </Section>
      )}

      <Section title="SQL">
        <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#fafafa' }}>
          <pre style={{ margin: 0, fontSize: '0.8rem', overflow: 'auto', maxHeight: 400 }}>{rule.inline_sql}</pre>
        </Paper>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Edit SQL via Claude / web UI form (full editor coming in v2). For now, use parameter overrides above.
        </Typography>
      </Section>
    </Box>
  );
};

// ===== Documents =====

// Auto-generated mirror files — not human-editable; hidden from the Documents
// tab so non-technical authors don't try to edit them in Word. The canonical
// source is the DB; `rule.sql` and `metadata.yaml` are regenerated on every
// rule update.
// Files in the rule folder come in three buckets:
//   1. Auto-generated mirrors (rule.sql, metadata.yaml) — hidden; the DB is canonical
//   2. Named slots (rule.docx, case_materials/complaint.docx, case_materials/prosecution_memo.docx)
//      — rendered as fixed cards even when the file doesn't exist yet
//   3. Everything else under case_materials/ — rendered under "Other documents"
const HIDDEN_FILE_PATHS = new Set(['rule.sql', 'metadata.yaml']);

const NAMED_SLOTS: { path: string; title: string; description: string; autoCreate?: boolean }[] = [
  {
    path: 'rule.docx',
    title: 'Rule narrative',
    description: 'Plain-English explanation of the rule. Auto-generated from the rule record; edit freely in Word.',
    autoCreate: true,
  },
  {
    path: 'case_materials/complaint.docx',
    title: 'Civil complaint',
    description: 'Upload an existing draft or create a new doc in Word.',
  },
  {
    path: 'case_materials/prosecution_memo.docx',
    title: 'Prosecution memo',
    description: 'Upload an existing draft or create a new doc in Word.',
  },
];
const NAMED_SLOT_PATHS = new Set(NAMED_SLOTS.map((s) => s.path));

const DocumentsPanel = ({ rule, cachedFiles, onFilesLoaded }: {
  rule: Rule;
  /** Files cached at the parent level — null means "not fetched yet for this rule". */
  cachedFiles: any[] | null;
  /** Push the latest file list up so it survives tab switches. */
  onFilesLoaded: (files: any[]) => void;
}) => {
  const [uploading, setUploading] = useState<string | null>(null);
  const [uploadErr, setUploadErr] = useState<string | null>(null);
  // Loading is *only* true on first visit for this rule. Subsequent visits
  // hydrate immediately from cachedFiles passed by the parent.
  const [loadingFiles, setLoadingFiles] = useState(cachedFiles === null);

  // Files come from a separate endpoint to avoid the ~900ms SharePoint
  // walk on every rule load. The Documents tab is the only place that needs them.
  const fetchFiles = async () => {
    setLoadingFiles(true);
    try {
      const res = await api.get(`/explore/rules/${rule.rule_id}/files/`);
      onFilesLoaded(res.data.files || []);
    } catch {
      // Network / Graph errors don't block the panel — show empty state instead
      onFilesLoaded([]);
    } finally {
      setLoadingFiles(false);
    }
  };

  useEffect(() => {
    // Skip fetch if the parent already has cached files for this rule.
    if (cachedFiles !== null) {
      setLoadingFiles(false);
      return;
    }
    fetchFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rule.rule_id]);

  const files = cachedFiles ?? [];

  const fileByPath = new Map<string, any>(files.map((f: any) => [(f.path || f.filename || ''), f]));
  const visibleFiles = files.filter(
    (f: any) => !HIDDEN_FILE_PATHS.has(f.path || f.filename || ''),
  );
  const otherFiles = visibleFiles.filter(
    (f: any) => !NAMED_SLOT_PATHS.has(f.path || f.filename || ''),
  );

  const handleUpload = async (path: string, file: File) => {
    setUploading(path);
    setUploadErr(null);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('path', path);
      await api.post(`/explore/rules/${rule.rule_id}/upload/`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      // Refresh the file list after upload
      await fetchFiles();
    } catch (e: any) {
      setUploadErr(e?.response?.data?.error || e.message || 'upload failed');
    } finally {
      setUploading(null);
    }
  };

  if (loadingFiles) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, gap: 2 }}>
        <CircularProgress size={28} />
        <Typography variant="body2" color="text.secondary">
          Loading documents from SharePoint…
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 900 }}>
      <Section title="Documents">
        <Typography variant="body2" color="text.secondary">
          Click <strong>Open in Word</strong> on any existing doc to edit it in your local Word app — autosaves back to SharePoint with full version history.
          For slots that don't exist yet, upload an existing file or create one directly in SharePoint.
        </Typography>
      </Section>

      {NAMED_SLOTS.map((slot) => (
        <NamedSlotCard
          key={slot.path}
          slot={slot}
          file={fileByPath.get(slot.path)}
          uploading={uploading === slot.path}
          onUpload={(file) => handleUpload(slot.path, file)}
        />
      ))}

      {otherFiles.length > 0 && (
        <Section title="Other documents">
          {otherFiles.map((f: any, i: number) => (
            <FileRow key={i} file={f} />
          ))}
        </Section>
      )}

      {/* Generic upload for "everything else" */}
      <Section title={otherFiles.length > 0 ? 'Upload another' : 'Upload another document'}>
        <UploadOther
          rule={rule}
          uploading={uploading === '__other__'}
          onUpload={(file) => handleUpload(`case_materials/${file.name}`, file)}
        />
      </Section>

      {uploadErr && <Alert severity="error">{uploadErr}</Alert>}

      {rule.sharepoint_folder_url && (
        <Typography variant="caption" color="text.secondary">
          SharePoint folder: <Link href={rule.sharepoint_folder_url} target="_blank" rel="noopener">{rule.sharepoint_folder_url}</Link>
        </Typography>
      )}
    </Box>
  );
};

const NamedSlotCard = ({ slot, file, uploading, onUpload }: {
  slot: { path: string; title: string; description: string; autoCreate?: boolean };
  file: any | undefined;
  uploading: boolean;
  onUpload: (file: File) => void;
}) => {
  if (file) {
    return (
      <Box>
        <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {slot.title}
        </Typography>
        <FileRow file={file} />
      </Box>
    );
  }
  return (
    <Box>
      <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {slot.title}
      </Typography>
      <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'rgba(0,0,0,0.02)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <DocIcon color="disabled" />
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              {slot.autoCreate ? 'Auto-generation pending' : 'Not yet drafted'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {slot.description}
            </Typography>
          </Box>
          <Button
            component="label"
            size="small"
            variant="outlined"
            disabled={uploading}
            sx={{ textTransform: 'none', minWidth: 110 }}
          >
            {uploading ? 'Uploading…' : 'Upload'}
            <input
              type="file"
              hidden
              accept=".docx,.doc,.pdf"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) onUpload(f);
                e.target.value = '';  // allow re-selecting same file
              }}
            />
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

const UploadOther = ({ rule, uploading, onUpload }: {
  rule: Rule; uploading: boolean; onUpload: (file: File) => void;
}) => {
  void rule;  // rule kept for future use (folder hint, etc.)
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Upload any other document (exhibits, supporting analysis, defendant correspondence, etc.). Lands under <code>case_materials/</code>.
          </Typography>
        </Box>
        <Button
          component="label"
          size="small"
          variant="outlined"
          disabled={uploading}
          sx={{ textTransform: 'none', minWidth: 110 }}
        >
          {uploading ? 'Uploading…' : 'Choose file'}
          <input
            type="file"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onUpload(f);
              e.target.value = '';
            }}
          />
        </Button>
      </Box>
    </Paper>
  );
};

const FileRow = ({ file }: { file: any }) => {
  const isOffice = !!file.office_protocol_url;
  const browserUrl = file.open_url || file.web_url;
  return (
    <Paper variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
      <DocIcon color="action" />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
          {file.path || file.filename}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {prettySize(file.size_bytes)} {file.last_modified && `· modified ${new Date(file.last_modified).toLocaleString()}`}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
        {isOffice ? (
          <>
            <Button
              size="small" variant="contained"
              href={file.office_protocol_url}
              sx={{ textTransform: 'none', minWidth: 130 }}
            >
              Open in Word
            </Button>
            {browserUrl && (
              <Link
                href={browserUrl}
                target="_blank"
                rel="noopener"
                underline="hover"
                sx={{ fontSize: '0.7rem' }}
              >
                view in browser
              </Link>
            )}
          </>
        ) : (
          <Button
            size="small" variant="outlined"
            href={browserUrl || '#'}
            target="_blank" rel="noopener"
            sx={{ textTransform: 'none' }}
          >
            Open
          </Button>
        )}
      </Box>
    </Paper>
  );
};

// ===== Results (per rule) — triage queue + scan history in one panel =====

interface ScanSummary {
  scan_id: string;
  scan_type: string;
  status: string;
  jurisdiction: string;
  lead_fire_count: number;
  leads_created_count: number;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
}

const ResultsPanel = ({ rule, navigate }: { rule: Rule; navigate: ReturnType<typeof useNavigate> }) => {
  const [scans, setScans] = useState<ScanSummary[]>([]);
  const [leads, setLeads] = useState<any[]>([]);
  const [loadingScans, setLoadingScans] = useState(true);
  const [loadingLeads, setLoadingLeads] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('open');
  // Track which scan rows are expanded. The latest scan auto-expands on
  // first load so "launch → see fires" works without an extra click.
  const [expandedScans, setExpandedScans] = useState<Set<string>>(new Set());
  const [autoExpanded, setAutoExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoadingScans(true);
    api.get(`/explore/rules/${rule.rule_id}/scans/`)
      .then((res) => {
        if (cancelled) return;
        const next: ScanSummary[] = res.data.scans || [];
        setScans(next);
        if (!autoExpanded && next.length > 0) {
          setExpandedScans(new Set([next[0].scan_id]));
          setAutoExpanded(true);
        }
      })
      .catch((e) => { if (!cancelled) setErr(e?.response?.data?.error || e.message || 'failed'); })
      .finally(() => { if (!cancelled) setLoadingScans(false); });
    return () => { cancelled = true; };
  }, [rule.rule_id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false;
    setLoadingLeads(true);
    // include_stale=true: show every lead ever produced by this rule,
    // even if the rule has since advanced past the version that fired it.
    // Triage shouldn't disappear just because we bumped a rule's version.
    // status filter: always send the param explicitly. "all" is interpreted
    // server-side as "no status filter" (otherwise a missing param defaults
    // to "open" for ad-hoc API callers).
    const params: Record<string, string> = {
      rule_id: rule.rule_id,
      limit: '200',
      include_stale: 'true',
      status: statusFilter,
    };
    api.get('/explore/leads/', { params })
      .then((res) => { if (!cancelled) setLeads(res.data.leads || []); })
      .catch(() => { /* leads error doesn't block the panel */ })
      .finally(() => { if (!cancelled) setLoadingLeads(false); });
    return () => { cancelled = true; };
  }, [rule.rule_id, statusFilter]);

  const toggleScan = (scanId: string) => {
    setExpandedScans((prev) => {
      const next = new Set(prev);
      if (next.has(scanId)) next.delete(scanId);
      else next.add(scanId);
      return next;
    });
  };

  if (err) return <Typography color="error">{err}</Typography>;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 1000 }}>
      {/* Scans — chronological run log; click a row to see that run's fires inline */}
      <Section title="Scans">
        {loadingScans ? (
          <CircularProgress size={20} />
        ) : scans.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No scans yet. Click <strong>Launch Scan</strong> above to run this rule.
            </Typography>
          </Paper>
        ) : (
          <Paper variant="outlined">
            {scans.map((s, i) => {
              const isOpen = expandedScans.has(s.scan_id);
              const isLatest = i === 0;
              return (
                <Box
                  key={s.scan_id}
                  sx={{
                    borderBottom: i < scans.length - 1 ? 1 : 0,
                    borderColor: 'divider',
                  }}
                >
                  <Box
                    onClick={() => toggleScan(s.scan_id)}
                    sx={{
                      px: 2, py: 1.5,
                      cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 1,
                      '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)' },
                    }}
                  >
                    {isOpen
                      ? <ExpandMoreIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                      : <ChevronRightIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {new Date(s.created_at).toLocaleString()}
                            {isLatest && (
                              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                · latest
                              </Typography>
                            )}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {s.lead_fire_count} fire{s.lead_fire_count === 1 ? '' : 's'}
                            {' · '}
                            {s.leads_created_count} new lead{s.leads_created_count === 1 ? '' : 's'}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Chip
                            label={s.status}
                            size="small"
                            color={
                              s.status === 'complete' ? 'success'
                              : s.status === 'failed' ? 'error'
                              : s.status === 'running' ? 'info' : 'default'
                            }
                            sx={{ height: 20 }}
                          />
                          <Chip label={s.scan_type} size="small" variant="outlined" sx={{ height: 20 }} />
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                  {isOpen && (
                    <Box
                      sx={{
                        px: 2, pb: 2,
                        backgroundColor: 'rgba(0,0,0,0.015)',
                        maxHeight: 600,
                        overflowY: 'auto',
                      }}
                    >
                      <ScanFiresInline scanId={s.scan_id} />
                    </Box>
                  )}
                </Box>
              );
            })}
          </Paper>
        )}
      </Section>

      {/* Triage queue — leads for this rule, status-filtered across all scans */}
      <Section title="Leads">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          {['open', 'pursued', 'closed', 'all'].map((s) => (
            <Chip
              key={s}
              label={s}
              size="small"
              variant={statusFilter === s ? 'filled' : 'outlined'}
              color={statusFilter === s ? 'primary' : 'default'}
              onClick={() => setStatusFilter(s)}
              sx={{ cursor: 'pointer', height: 24 }}
            />
          ))}
        </Box>
        {loadingLeads ? (
          <CircularProgress size={20} />
        ) : leads.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No {statusFilter === 'all' ? '' : statusFilter} leads on this rule.
            </Typography>
          </Paper>
        ) : (
          <Paper variant="outlined">
            {leads.map((l: any, i: number) => (
              <LeadRow
                key={l.lead_id}
                lead={l}
                showLastFired
                isLast={i === leads.length - 1}
              />
            ))}
          </Paper>
        )}
      </Section>
    </Box>
  );
};

// Expandable lead row used in both the Scans accordion (fires) and the Leads
// section. Click the row to expand the inline triage card (LeadInline)
// beneath it — provider context, notes, and action buttons all in place.
const LeadRow = ({ lead, isLast, showLastFired = false }: {
  lead: any; isLast: boolean; showLastFired?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const amount = parseFloat(lead.amount_usd || '0');
  return (
    <Box
      sx={{
        borderBottom: isLast ? 0 : 1,
        borderColor: 'divider',
      }}
    >
      <Box
        onClick={() => setOpen((v) => !v)}
        sx={{
          px: 2, py: 1.5,
          cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 1,
          '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)' },
        }}
      >
        {open
          ? <ExpandMoreIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
          : <ChevronRightIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
            {lead.display_name || `${lead.subject_type}:${lead.subject_id}`}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {lead.subject_type}:{lead.subject_id}
            {showLastFired && lead.last_fired_at
              ? ` · last fired ${new Date(lead.last_fired_at).toLocaleDateString()}`
              : ''}
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          ${amount.toLocaleString(undefined, { maximumFractionDigits: 0 })}
        </Typography>
        <Chip label={lead.status} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
      </Box>
      {open && (
        <Box sx={{ px: 2, pb: 2, backgroundColor: 'rgba(0,0,0,0.015)' }}>
          <LeadInline leadId={lead.lead_id} />
        </Box>
      )}
    </Box>
  );
};

// Inline fires table for an expanded scan row in the Results tab. Each row
// uses LeadRow so triage actions work in place without leaving the rule.
const ScanFiresInline = ({ scanId }: { scanId: string }) => {
  const [leads, setLeads] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/explore/scans/${scanId}/leads/`)
      .then((res) => { if (!cancelled) setLeads(res.data.leads || []); })
      .catch((e) => { if (!cancelled) setErr(e?.response?.data?.error || e.message || 'failed'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [scanId]);

  if (loading) return <Box sx={{ py: 1.5 }}><CircularProgress size={16} /></Box>;
  if (err) return <Typography color="error" variant="caption">{err}</Typography>;
  if (leads.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', py: 1 }}>
        No fires recorded for this scan.
      </Typography>
    );
  }

  return (
    <Paper variant="outlined" sx={{ mt: 1, backgroundColor: 'background.paper' }}>
      {leads.map((l: any, i: number) => (
        <LeadRow key={l.lead_id} lead={l} isLast={i === leads.length - 1} />
      ))}
    </Paper>
  );
};


// ===== History =====

const HistoryPanel = ({ rule }: { rule: Rule }) => {
  const [versions, setVersions] = useState<Version[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get(`/explore/rules/${rule.rule_id}/versions/`)
      .then((res) => { if (!cancelled) setVersions(res.data.versions || []); })
      .catch(() => { /* swallow */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [rule.rule_id]);

  if (loading) return <CircularProgress size={20} />;
  if (versions.length === 0) {
    return <Typography color="text.secondary">No version history.</Typography>;
  }

  return (
    <Section title="Versions">
      {versions.map((v) => (
        <Box key={v.version} sx={{ mb: 2, pb: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon fontSize="small" color="action" />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              v{v.version}
              {v.version === rule.current_version && (
                <Chip label="current" size="small" color="success" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
              )}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {new Date(v.created_at).toLocaleString()}
              {v.created_by && ` · ${v.created_by}`}
            </Typography>
          </Box>
          {v.change_summary && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, ml: 3 }}>
              {v.change_summary}
            </Typography>
          )}
        </Box>
      ))}
    </Section>
  );
};

// ===== Status transition =====

const STATUS_TRANSITIONS: Record<string, { label: string; targets: string[]; verb: string }> = {
  proposed: { label: 'Proposed', targets: ['active', 'archived'],   verb: 'Activate' },
  active:   { label: 'Active',   targets: ['archived', 'proposed'], verb: 'Archive' },
  archived: { label: 'Archived', targets: ['proposed', 'active'],   verb: 'Re-activate' },
};

const StatusChip = ({ rule, onChanged }: { rule: Rule; onChanged: (r: Rule) => void }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [dialog, setDialog] = useState<{ open: boolean; target: string }>({ open: false, target: '' });

  const targets = STATUS_TRANSITIONS[rule.status]?.targets || [];

  return (
    <>
      <Chip
        label={rule.status}
        size="small"
        color={statusColor(rule.status)}
        onClick={(e) => setAnchor(e.currentTarget)}
        clickable
      />
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {targets.map((t) => (
          <MenuItem
            key={t}
            onClick={() => {
              setAnchor(null);
              setDialog({ open: true, target: t });
            }}
          >
            Change to <strong style={{ marginLeft: 4 }}>{t}</strong>
          </MenuItem>
        ))}
      </Menu>
      <StatusTransitionDialog
        open={dialog.open}
        target={dialog.target}
        rule={rule}
        onClose={() => setDialog({ open: false, target: '' })}
        onSuccess={(r) => { setDialog({ open: false, target: '' }); onChanged(r); }}
      />
    </>
  );
};

const StatusTransitionDialog = ({
  open, target, rule, onClose, onSuccess,
}: {
  open: boolean;
  target: string;
  rule: Rule;
  onClose: () => void;
  onSuccess: (r: Rule) => void;
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    setSubmitting(true); setErr(null);
    try {
      const res = await api.post(`/explore/rules/${rule.rule_id}/update/`, {
        fields: { status: target },
        change_summary: `Status: ${rule.status} → ${target}`,
      });
      onSuccess(res.data);
    } catch (e: any) {
      setErr(e?.response?.data?.error || e.message || 'failed');
    } finally { setSubmitting(false); }
  };

  const description: Record<string, string> = {
    active: 'Activates this rule for production scans. Stamps approved_by + approved_at. Production scans (launch_scan) will accept it.',
    proposed: 'Returns this rule to proposed — production scans will refuse it. Existing leads remain in place.',
    archived: 'Marks this rule as no longer in use. New scans will refuse it; existing leads stay. Reversible.',
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Change status: {rule.status} → {target}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary">
          {description[target]}
        </Typography>
        {err && <Alert severity="error" sx={{ mt: 2 }}>{err}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={submitting}
          color={target === 'active' ? 'success' : 'primary'}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{ textTransform: 'none' }}
        >
          {submitting ? 'Saving…' : `Confirm`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DeleteRuleDialog = ({ open, onClose, rule, onDeleted }: {
  open: boolean; onClose: () => void; rule: Rule; onDeleted: () => void;
}) => {
  const [reason, setReason] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (open) { setReason(''); setConfirmText(''); setErr(null); }
  }, [open]);

  const canSubmit = confirmText.trim() === rule.rule_id;

  const submit = async () => {
    if (!canSubmit) { setErr(`Type "${rule.rule_id}" to confirm.`); return; }
    setSubmitting(true); setErr(null);
    try {
      await api.delete(`/explore/rules/${rule.rule_id}/`, { data: { reason } });
      onDeleted();
    } catch (e: any) {
      setErr(e?.response?.data?.error || e.message || 'failed');
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Delete rule</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Soft-deletes <strong>{rule.rule_id}</strong>. The rule disappears from
          lists and scans, but every existing lead, case, scan, and version
          snapshot is preserved unchanged. Reversible by an admin.
        </Typography>
        <TextField
          fullWidth
          size="small"
          label="Reason (optional)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          size="small"
          label={`Type "${rule.rule_id}" to confirm`}
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
        />
        {err && <Alert severity="error" sx={{ mt: 2 }}>{err}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button
          variant="contained" color="error"
          onClick={submit}
          disabled={submitting || !canSubmit}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{ textTransform: 'none' }}
        >
          {submitting ? 'Deleting…' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ===== Helpers =====

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Box>
    <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

function statusColor(status: string): 'default' | 'success' | 'warning' | 'error' {
  switch (status) {
    case 'active': return 'success';
    case 'proposed': return 'warning';
    case 'archived': return 'default';
    default: return 'default';
  }
}

function prettySize(bytes?: number): string {
  if (!bytes) return '';
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

function formatCell(v: any): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

export default RuleDetail;
